import { ActionDropdown, getActions } from 'actions';
import type { Json, Trigger } from '@norscom/nap-shared';
import {
  DetailsBlock,
  DetailsGrid,
  DetailsRow,
  DetailsSection,
  DetailsSentence,
  RecordDetailsModel,
  RecordDetailsPane,
  ViewDateTime,
  ViewEventReference,
  ViewFolderPath,
  ViewNodeDefinitionReference,
  ViewQueueReference,
  ViewStatus,
  ViewString,
  ViewZoneReference,
} from 'details';
import { toTriggerRefEntity } from '../triggerActions';
import { RESTART_FIRE_MODE_LABELS, triggerActionLabel, triggerTypeLabel } from '../triggerLabels';
import { actionSummary, conditionSummary, DEFAULT_QUEUE, monitoredZones, parameterOverrides, parameterValue, restartFireModeApplies } from './triggerSummary';

/** A `submit_child`'s parameter overrides, a row each; the stored JSON when they are not a list of overrides. */
const ParameterRows = ({ parameters }: { readonly parameters: Json }) => {
  const overrides = parameterOverrides(parameters);
  if (overrides === null) {
    return (
      <DetailsRow label="Parameters">
        <ViewString value={JSON.stringify(parameters)} code />
      </DetailsRow>
    );
  }
  return (
    <>
      {overrides.map((parameter, index) => (
        // By position: nothing makes the stored names unique.
        <DetailsRow key={index} label={parameter.name}>
          <ViewString value={parameterValue(parameter.value)} code />
        </DetailsRow>
      ))}
    </>
  );
};

const TriggerDetailsContent = ({ trigger }: { readonly trigger: Trigger }) => {
  const sc = trigger.status_change;
  const type = <ViewString value={triggerTypeLabel(trigger.type)} />;

  return (
    <>
      <DetailsBlock>
        <DetailsGrid>
          <DetailsRow label="Id">{trigger.id}</DetailsRow>
          <DetailsRow label="Name">
            <ViewString value={trigger.name} />
          </DetailsRow>
          <DetailsRow label="Zone">
            <ViewZoneReference value={trigger.zone} />
          </DetailsRow>
          <DetailsRow label="Folder">
            <ViewFolderPath value={trigger.folder} />
          </DetailsRow>
        </DetailsGrid>
      </DetailsBlock>

      {sc === null ? (
        <DetailsSection title="When">
          <DetailsGrid>
            <DetailsRow label="Type">{type}</DetailsRow>
            <DetailsSentence>
              <span className="text-muted">This trigger has no condition configured.</span>
            </DetailsSentence>
          </DetailsGrid>
        </DetailsSection>
      ) : (
        <>
          <DetailsSection title="When" summary={conditionSummary(trigger, sc)}>
            <DetailsGrid>
              <DetailsRow label="Type">{type}</DetailsRow>
              <DetailsRow label="Node">
                <ViewNodeDefinitionReference value={sc.node_definition} placeholder="Any node" />
              </DetailsRow>
              <DetailsRow label="From status">
                <ViewStatus value={sc.from_status} placeholder="Any status" />
              </DetailsRow>
              <DetailsRow label="To status">
                <ViewStatus value={sc.to_status} placeholder="Any status" />
              </DetailsRow>
              <DetailsRow label="Zones">
                <ViewString value={monitoredZones(trigger)} />
              </DetailsRow>
              {restartFireModeApplies(sc) && (
                <DetailsRow label="Auto-restart firing">
                  <ViewString value={RESTART_FIRE_MODE_LABELS[sc.restart_fire_mode]} />
                </DetailsRow>
              )}
            </DetailsGrid>
          </DetailsSection>

          <DetailsSection title="Then" summary={actionSummary(sc)}>
            <DetailsGrid>
              <DetailsRow label="Action">
                <ViewString value={triggerActionLabel(sc.action)} />
              </DetailsRow>
              {sc.action === 'raise_event' && (
                <DetailsRow label="Event">
                  <ViewEventReference value={sc.target_event} />
                </DetailsRow>
              )}
              {sc.action === 'submit_child' && (
                <>
                  <DetailsRow label="Node definition">
                    <ViewNodeDefinitionReference value={sc.target_node_definition} />
                  </DetailsRow>
                  <DetailsRow label="Queue">
                    <ViewQueueReference value={sc.queue} placeholder={DEFAULT_QUEUE.charAt(0).toUpperCase() + DEFAULT_QUEUE.slice(1)} />
                  </DetailsRow>
                  <DetailsRow label="Timezone">
                    <ViewString value={sc.timezone} placeholder="The configured default" />
                  </DetailsRow>
                  <ParameterRows parameters={sc.parameters} />
                </>
              )}
            </DetailsGrid>
          </DetailsSection>
        </>
      )}

      <DetailsSection
        title="History"
        defaultCollapsed
        summary={
          trigger.updated_at !== null && (
            <>
              Last updated <ViewDateTime value={trigger.updated_at} />
            </>
          )
        }
      >
        <DetailsGrid>
          <DetailsRow label="Created">
            <ViewDateTime value={trigger.created_at} />
          </DetailsRow>
          <DetailsRow label="Updated">
            <ViewDateTime value={trigger.updated_at} />
          </DetailsRow>
        </DetailsGrid>
      </DetailsSection>
    </>
  );
};

export interface TriggerDetailsProps {
  /** Owned by the list, so the list's refresh reaches it too. */
  readonly model: RecordDetailsModel<Trigger>;
  readonly onClose: () => void;
  /** Called after an action changed or deleted the trigger: re-reads the list and this pane alike. */
  readonly onChanged: () => void;
}

/** The details pane for one trigger (#1335). */
export const TriggerDetails = ({ model, onClose, onChanged }: TriggerDetailsProps) => (
  <RecordDetailsPane
    model={model}
    icon="zap"
    kind="Trigger"
    onClose={onClose}
    name={(trigger) => trigger.name}
    actions={(trigger) => (
      <ActionDropdown
        actions={getActions({ entities: [toTriggerRefEntity(trigger)], refresh: onChanged })}
        items={['edit', 'addToTransportTrigger', 'delete']}
        align="end"
      />
    )}
  >
    {(trigger) => <TriggerDetailsContent trigger={trigger} />}
  </RecordDetailsPane>
);
